import { cn } from '@/lib/cn'
import { initials } from '@/lib/format'
import { avatarColorVar, type AvatarColorName } from '@/lib/avatarColors'

/**
 * Solid fill avatar with initial letter. Defaults to brand fill.
 */
export function Avatar({
  name,
  size = 'md',
  color,
  src,
  className,
}: {
  name: string
  size?: 'sm' | 'md' | 'lg'
  /** One of the fixed avatar swatch names — see `lib/avatarColors.ts`. */
  color?: AvatarColorName
  /** An image to show instead of initials-on-colour, e.g. the bot's logo. */
  src?: string
  className?: string
}) {
  const sizes = {
    sm: 'h-6 w-6 text-[10px]',
    md: 'h-8 w-8 text-xs',
    lg: 'h-10 w-10 text-base',
  } as const

  if (src) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden
        className={cn('inline-block shrink-0 rounded-full object-cover select-none', sizes[size], className)}
      />
    )
  }

  return (
    <span
      // Decorative: the name is always rendered beside it. Announcing initials
      // to a screen reader on top of the adjacent name would be redundant.
      aria-hidden
      style={color ? { backgroundColor: avatarColorVar(color) } : undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full',
        'font-semibold text-on-primary select-none',
        !color && 'bg-primary-strong',
        sizes[size],
        className,
      )}
    >
      {initials(name)}
    </span>
  )
}
